import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, defer, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import { Store } from '@ngxs/store';
import { StreamEvent } from '../models/websocket-interfaces';
import { Client, IMessage, StompHeaders, StompSubscription } from '@stomp/stompjs';
import { catchError, distinctUntilChanged, filter, retryWhen, shareReplay, switchMap, take } from 'rxjs/operators';
import { AuthState } from '../../../../core/auth/state/auth.state';
import { AuthActions } from '../../../../core/auth/state/auth.actions';
import { environment } from '../../../../../environments/environment';
import { ChatbotActions } from '../state/chatbot.actions';

@Injectable({
  providedIn: 'root'
})
export class ChatbotWebsocketService {
  private eventsSubject = new Subject<StreamEvent>();
  private client: Client | null = null;
  private queueSub?: StompSubscription;
  private conversationId?: string;
  private _retries = 0;
  private _closedByClient = false;
  private _autoReconnect = false; // reconnect only while waiting for a response
  private _reconnecting = false; // guard against parallel attempts
  private _maxReconnectsWhileStreaming = 12;
  private reconnectSub?: Subscription;

  /** Connection state */
  private connected$ = new BehaviorSubject<boolean>(false);
  private readonly WS_URL = environment.websocketUrl;

  constructor(
    private store: Store,
    private zone: NgZone
  ) {}

  /** Connect and subscribe for the given conversation */
  connect$(conversationId: string): Observable<boolean> {
    if (this.client?.active && this.conversationId === conversationId) {
      return this.connected$.pipe(distinctUntilChanged());
    } else if (this.client?.active && this.conversationId !== conversationId) {
      this.disconnect(); // ensure old socket is closed first
    }

    const ensureToken$ = defer(() =>
      this.store.dispatch(new AuthActions.EnsureToken()).pipe(
        // We don't rely on the boolean; we rely on header$ below.
        catchError(() => of(null))
      )
    );
    const header$ = this.store.select(AuthState.header).pipe(
      filter((h): h is string => !!h),
      take(1)
    );

    const connectOnce$ = ensureToken$.pipe(
      switchMap(() => header$),
      switchMap(
        (authHeader) =>
          new Observable<boolean>((observer) => {
            const client = new Client({
              webSocketFactory: () => new WebSocket(this.WS_URL),
              connectHeaders: { Authorization: authHeader } as StompHeaders,
              heartbeatIncoming: 10000,
              heartbeatOutgoing: 10000,
              reconnectDelay: 0,
              debug: () => {}
            });

            client.onConnect = () => {
              this.zone.run(() => {
                this.client = client;
                this.conversationId = conversationId;
                this.connected$.next(true);

                const dest = `/user/queue/ai-chat/${encodeURIComponent(conversationId)}`;
                this.queueSub = client.subscribe(dest, (msg: IMessage) => {
                  this.zone.run(() => {
                    try {
                      const evt = JSON.parse(msg.body) as StreamEvent;
                      if (evt.type === 'start') this._autoReconnect = true;
                      this.eventsSubject.next(evt);
                      switch (evt.type) {
                        case 'start':
                          this.store.dispatch(new ChatbotActions.WSStart(evt.conversationId, evt.messageId));
                          break;
                        case 'chunk':
                          this.store.dispatch(
                            new ChatbotActions.WSChunk(
                              evt.conversationId,
                              evt.data || '',
                              evt.seq ?? null,
                              evt.messageId
                            )
                          );
                          break;
                        case 'complete':
                          this.store.dispatch(new ChatbotActions.WSComplete(evt.conversationId, evt.messageId));
                          break;
                        case 'error':
                          this.store.dispatch(
                            new ChatbotActions.WSError(evt.conversationId, evt.error || 'Unknown error')
                          );
                          break;
                      }
                    } catch (e) {
                      console.warn('[WS] bad JSON in message', e);
                    }
                  });
                });
                this._retries = 0;
                observer.next(true);
              });
            };

            client.onStompError = (frame) => {
              this.zone.run(() => {
                this.store.dispatch(new ChatbotActions.WSError(this.conversationId!, frame.body || 'STOMP error'));
                this.eventsSubject.next({
                  type: 'error',
                  seq: null,
                  messageId: null,
                  conversationId,
                  error: frame.body || 'STOMP error',
                  data: null
                });
                // propagate error to trigger retryWhen
                observer.error(new Error('STOMP error'));
              });
            };

            client.onWebSocketClose = () => {
              this.zone.run(() => {
                const wasClosedByClient = this._closedByClient;

                if (!wasClosedByClient && this._autoReconnect) {
                  // no error dispatch; we're going to try to recover
                  this.tryReconnect();
                } else if (!wasClosedByClient) {
                  // only treat as error when we aren't auto-reconnecting
                  this.store.dispatch(new ChatbotActions.WSError(this.conversationId!, 'WebSocket closed'));
                }

                // reset flag if this close event was initiated by the user (prevents race conditions)
                if (wasClosedByClient) {
                  this._closedByClient = false;
                }
              });
            };

            client.onWebSocketError = (evt) => {
              this.zone.run(() => {
                console.error('[WS] WebSocket error', evt);
                observer.error(new Error('WebSocket error'));
              });
            };

            client.activate();

            return () => {};
          })
      )
    );

    return connectOnce$.pipe(
      retryWhen((err$) =>
        err$.pipe(
          switchMap(() => {
            this._retries += 1;
            if (this._retries > 5) {
              return throwError(() => new Error('WS connect retries exceeded'));
            }
            // Backoff: 1s, 2s, 4s, 8s (cap 8s). Retry up to 5 times, reset on successful connect.
            const delayMs = Math.min(8000, 1000 * Math.pow(2, this._retries - 1));
            return timer(delayMs); // cancellable; no dangling timeouts
          })
        )
      ),
      switchMap(() => this.connected$),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  public disconnect(): void {
    this._closedByClient = true;
    this._autoReconnect = false;
    this._reconnecting = false;

    if (this.reconnectSub) {
      this.reconnectSub.unsubscribe();
      this.reconnectSub = undefined;
    }

    try {
      this.queueSub?.unsubscribe();
    } catch {}
    this.queueSub = undefined;
    const c = this.client;
    this.conversationId = undefined;
    this.client = null;
    this.connected$.next(false);
    if (c?.active)
      c.deactivate().catch((error) => {
        console.warn('[WS] Error during client deactivation:', error);
      });
  }

  public send(conversationId: string, message: string, entityContextRoute: string): void {
    if (!this.client || !this.connected$.value) return;
    if (this.conversationId !== conversationId) return; // avoid sending to the wrong conversation
    this._autoReconnect = true;
    this.client.publish({
      destination: '/app/ai-chat',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ conversationId, message, entityContext: { route: entityContextRoute } })
    });
  }

  public cancel(conversationId: string): void {
    if (!this.client || !this.connected$.value) return;
    this.client.publish({
      destination: '/app/ai-chat/cancel',
      headers: { 'content-type': 'text/plain' },
      body: conversationId
    });
  }

  private tryReconnect() {
    if (this._reconnecting || !this.conversationId) return;
    this._reconnecting = true;

    const attempt = () => {
      // bump max retries while streaming
      const max = this._maxReconnectsWhileStreaming;
      const n = Math.min(8000, 1000 * Math.pow(2, this._retries)); // 1s,2s,4s,8s cap 8s
      this._retries++;

      this.connect$(this.conversationId!)
        .pipe(filter(Boolean), take(1))
        .subscribe({
          next: () => {
            this._retries = 0;
            this._reconnecting = false;
            this.reconnectSub = undefined;
          },
          error: () => {
            if (this._retries >= max || !this._autoReconnect) {
              // give up; surface error to state
              this._reconnecting = false;
              this.reconnectSub = undefined;
              this.store.dispatch(new ChatbotActions.WSError(this.conversationId!, 'Reconnect attempts exceeded'));
              return;
            }
            // schedule next attempt
            this.reconnectSub = undefined;
            this.reconnectSub = timer(n).subscribe(() => {
              this.reconnectSub = undefined;
              attempt();
            });
          }
        });
    };

    attempt();
  }
}
